import React from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import { DateTime } from 'luxon';

import withStyles from '@mui/styles/withStyles';
import Link from '@mui/material/Link';

import { removeFirmOutreach } from '../store/slices/outreachesSlice';
import { removeOutreach, removeOutreachFrom } from '../store/slices/outreachListsSlice';
import { updateMissive, deleteMissiveWithId } from '../store/slices/missivesSlice';
import { removeNoteOnPerson } from '../store/slices/notesSlice';
import { clearTaskAccomplishmentInfo } from '../store/slices/dashboardStatsSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import { showAlert, createStdRequest, apiFetch, timeStringForDateTime } from '../utils/utils';

import activityListStyles from '../assets/jss/crm/components/activityItemStyles';

import DA from '../store/ReduxAccess';
import ActivityEditorPanel from './ActivityEditorPanel';
import NoteEditorPanel from './NoteEditorPanel';
import FullSmsPanel from './FullSmsPanel';
import AlertModal from './AlertModal';
import CallRailPlayerWidget from './CallRailPlayerWidget';
import ReduxLoader from '../store/ReduxLoader';

/*
 * Call with something like
    <ActivityItem
      key={something unique}
      i={i}
      groupLabel={groupLabel}
      activity={activity}
      functionToShowSubject={function to show subject}
      rerenderPageFunc={function to rerender the page}
      page={<somebody that implements buildActivityGroupsFromReduxStore()>}
      contacts={[<list of contacts to show in expanded activity items>]}
      client={<subject>}
    />;
*/

class ActivityItem extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      expandedActivities: [],
    };
  }

  getConnectivePhraseForActivityMode(mode) {
    switch (mode) {
      case 'phone':
        return ' logged a phone call with ';
      case 'voicemail':
        return ' logged a voicemail to ';
      case 'email':
        return ' logged an email to ';
      case 'text':
        return ' sent a text to ';
      case 'meeting':
        return ' met with ';
      case 'tour':
        return ' gave a tour to ';
      case 'scheduleAssessment':
        return ' scheduled an assessment with ';
      case 'completeAssessment':
        return ' completed assessment with ';
      case 'note':
        return ' wrote a note about ';
      case 'toNone':
        return 'Status Init for ';
      case 'toProspect':
      case 'toClient':
      case 'toAlum':
        return 'Status Change for ';
      default:
        return ' communicated with ';
    }
  }

  getTimeElementStrings(dateTime) {
    const dateIsoString = dateTime.toISO();
    const mediumDate = dateTime.toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY);
    const simpleTime = timeStringForDateTime(dateTime);
    const dateDisplayString = mediumDate + ' at ' + simpleTime;
    const dateTitleString = dateDisplayString;
    return { dateIsoString, dateTitleString, dateDisplayString };
  }

  toggleExpansion(activity) {
    const isExpandable = activity.activityType !== 'statusTransition' && !this.isCallRailCall(activity);

    if (!isExpandable) {
      return;
    }

    const activityId = activity.id;
    const index = this.state.expandedActivities.indexOf(activityId);
    if (index >= 0) {
      const listCopy = this.state.expandedActivities.slice(0);
      listCopy.splice(index, 1);
      this.setState({ expandedActivities: listCopy });
    } else {
      const newList = this.state.expandedActivities.concat(activityId);
      this.setState({ expandedActivities: newList });
    }
  }

  localUpdateNoteContent(note, newContent) {
    return { ...note, content: newContent };
  }

  localUpdateMissiveContent(missive, newContent, typeStr) {
    return { ...missive, content: newContent, description: newContent, typeStr };
  }

  updateNotelyActivity(activity, newContent) {
    const noteId = activity.id;
    const note = DA.getNoteWithId(noteId);
    const updatedNote = this.localUpdateNoteContent(note, newContent);
    const uri = `/api/note/${noteId}`;
    const request = createStdRequest(uri, 'PUT', updatedNote);
    const actionOnGoodResponse = (json) => {
      ReduxLoader.addOrUpdateNotesOnPersons([json]);
      this.toggleExpansion(activity);
      this.props.rerenderPageFunc();
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  updateGenericActivity(activity, newContent, newTypeStr) {
    const firmId = DA.getFirmId();
    const outreachId = activity.id;
    const subjectId = activity.subjectId;
    const outreach = DA.getOutreachByIds(subjectId, outreachId) || DA.getOutreachFromById(outreachId, subjectId);
    const missiveId = outreach.missiveId;
    const missive = DA.getMissiveById(missiveId);
    const updatedMissive = this.localUpdateMissiveContent(missive, newContent, newTypeStr);
    const uri = `/api/firm/${firmId}/missive/${missiveId}`;
    const request = createStdRequest(uri, 'PUT', updatedMissive);
    const actionOnGoodResponse = (json) => {
      this.props.updateMissive(json);
      this.toggleExpansion(activity);
      this.props.rerenderPageFunc();
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  deleteNoteWithId(noteId) {
    const note = DA.getNoteWithId(noteId);
    const uri = `/api/note/${noteId}`;
    const request = createStdRequest(uri, 'DELETE');
    const actionOnGoodResponse = () => {
      this.props.clearTaskAccomplishmentInfo();
      this.props.removeNoteOnPerson(note.noteId);
      this.props.rerenderPageFunc();
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  deleteGenericActivityRecord(activity) {
    const outreachId = activity.id;
    const { subjectId, agentId } = activity;
    const isFirmOutreach = DA.isFirmOutreach(outreachId);
    let outreach = null;
    let direction = 'to';
    if (isFirmOutreach) {
      outreach = DA.getOutreachById(outreachId);
      const category = DA.getFirmOutreachCategory(outreachId);
      if (category === 'fromFirmContact') {
        direction = 'from';
      }
    } else {
      outreach = DA.getOutreachByIds(subjectId, outreachId);
      if (!outreach) {
        outreach = DA.getOutreachFromById(outreachId, agentId);
        direction = 'from';
      }
    }
    const firmId = DA.getFirmId();
    const uri = `/api/firm/${firmId}/outreach/${outreachId}`;
    const request = createStdRequest(uri, 'DELETE');
    const actionOnGoodResponse = () => {
      this.props.clearTaskAccomplishmentInfo();
      if (isFirmOutreach) {
        this.props.removeFirmOutreach(outreachId);
      }
      if (direction === 'to') {
        this.props.removeOutreach({ personId: subjectId, outreachId });
      } else {
        this.props.removeOutreachFrom({ personId: agentId, outreachId });
      }
      const missiveId = outreach.missiveId;
      this.deleteMissiveWithId(missiveId);
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  deleteMissiveWithId(missiveId) {
    const firmId = DA.getFirmId();
    const uri = `/api/firm/${firmId}/missive/${missiveId}`;
    const request = createStdRequest(uri, 'DELETE');
    const actionOnGoodResponse = () => {
      const missive = DA.getMissiveById(missiveId);
      this.props.deleteMissiveWithId(missive.missiveId);
      this.props.rerenderPageFunc();
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  updateActivityContent(activity, newContent) {
    switch (activity.activityMode) {
      case 'note':
        this.updateNotelyActivity(activity, newContent);
        break;
      case 'meeting':
        this.updateGenericActivity(activity, newContent, 'Meeting');
        break;
      case 'tour':
        this.updateGenericActivity(activity, newContent, 'Tour');
        break;
      case 'scheduleAssessment':
        this.updateGenericActivity(activity, newContent, 'ScheduleAssessment');
        break;
      case 'completeAssessment':
        this.updateGenericActivity(activity, newContent, 'CompleteAssessment');
        break;
      case 'phone':
        this.updateGenericActivity(activity, newContent, 'PhoneCall');
        break;
      case 'voicemail':
        this.updateGenericActivity(activity, newContent, 'PersonalVoiceMailOrMessage');
        break;
      case 'email':
        this.updateGenericActivity(activity, newContent, 'PersonalEmail');
        break;
      default:
        showAlert(this, "Don't know how to update activity with activityMode: " + activity.activityMode);
    }
  }

  deleteActivity(activity) {
    switch (activity.activityMode) {
      case 'note':
        this.deleteNoteWithId(activity.id);
        break;
      case 'meeting':
      case 'tour':
      case 'scheduleAssessment':
      case 'completeAssessment':
      case 'phone':
      case 'voicemail':
      case 'email':
        this.deleteGenericActivityRecord(activity);
        break;
      default:
        showAlert(this, "Don't know how to delete activity with activityMode: " + activity.activityMode);
    }
  }

  ignoreClickIfExpanded(event, isExpanded) {
    if (isExpanded) {
      event.stopPropagation();
    }
  }

  ignoreClickIfExpandedOrIsCallRailCall(event, isExpanded, isCallRailCall) {
    if (isExpanded || isCallRailCall) {
      event.stopPropagation();
    }
  }

  isCallRailCall(activity) {
    const missiveId = activity.missiveId;
    if (!missiveId) {
      return false;
    }
    const missive = DA.getMissiveById(missiveId);
    const callRailCallId = missive && missive.callRailCallId;
    return !!callRailCallId;
  }

  render() {
    // TODO still need referrerOrgId and activityColumn
    const { classes, i, groupLabel, activity, functionToShowSubject, page, contacts, client } = this.props;

    const isCallRailCall = this.isCallRailCall(activity);
    let call = null;
    let missiveName = '';
    if (isCallRailCall) {
      const missive = DA.getMissiveById(activity.missiveId);
      missiveName = missive.name;
      call = DA.getCallRailCall(missive.callRailCallId);
    }

    const firstInGroup = i === 0;
    const label = firstInGroup ? groupLabel : '';
    const isExpanded = this.state.expandedActivities.indexOf(activity.id) >= 0;

    const itemContentClasses = cx({
      [classes.activityItem_callRailDiv]: isCallRailCall,
      [classes.activityItem_content]: !isCallRailCall,
      [classes.u_ellipsis]: !isExpanded,
    });

    const itemClasses = cx(
      classes.activityItem,
      {
        [classes.activityItem_note]: activity.activityType === 'note' && !isCallRailCall,
        [classes.activityItem_correspondence]: activity.activityType === 'correspondence',
        [classes.activityItem_meeting]: activity.activityType === 'meeting',
        [classes.activityItem_transition]: activity.activityType === 'statusTransition',
        [classes.activityItem_callRailCall]: isCallRailCall,
      },
      classes.u_boxShadow,
      { isLabeled: firstInGroup, isExpanded: isExpanded }
    );
    const imageClasses = cx(classes.activityImage, {
      isNote: activity.activityType === 'note',
      isCorrespondence: activity.activityType === 'correspondence',
      isMeeting: activity.activityType === 'meeting',
      isTransition: activity.activityType === 'statusTransition',
    });
    const primaryClasses = cx(
      classes.activityImage_primary,
      classes.profileImageComponent,
      classes.profileImageComponent_circle
    );
    const am = activity.activityMode;
    const iconClasses = cx('zmdi', {
      'zmdi-phone': am === 'phone',
      'zmdi-voicemail': am === 'voicemail',
      'zmdi-email': am === 'email',
      'zmdi-smartphone-iphone': am === 'text',
      'zmdi-accounts': am === 'meeting',
      // 'zmdi-bus': am === "tour",
      'zmdi-walk': am === 'tour',
      'zmdi-assignment': am === 'scheduleAssessment',
      'zmdi-assignment-check': am === 'completeAssessment',
      'zmdi-file-text': am === 'note',
      'zmdi-account': am === 'toNone' || am === 'none',
      'zmdi-account-o': am === 'toProspect',
      'zmdi-account-box': am === 'toClient',
      'zmdi-graduation-cap': am === 'toAlum',
    });
    const connectivePhrase = this.getConnectivePhraseForActivityMode(activity.activityMode);
    const { dateIsoString, dateTitleString, dateDisplayString } = this.getTimeElementStrings(activity.dateTime);
    const isNote = activity.activityMode === 'note';
    const isText = activity.activityMode === 'text';
    const referrerOrgId = activity.direction === 'To' ? activity.receivingOrgId : activity.sengingOrgId;

    const agentNameToUse = activity.agentName.startsWith('null') ? 'system' : activity.agentName;

    return (
      <div>
        <AlertModal show={this.state.shouldShowAlertModal} renderer={this} text={this.state.alertModalText} />

        <div className={itemClasses} onClick={() => this.toggleExpansion(activity)}>
          <div className={cx(classes.activityItem_label, classes.ac_h4)}>{label}</div>
          <div className={classes.activityItem_container}>
            <div className={classes.activityItem_profileSection}>
              <div className={classes.activityItem_profileImage}>
                <div className={imageClasses}>
                  <div className={primaryClasses}>
                    <div className={classes.profileImageComponent_initials}>{activity.agentInitials}</div>
                  </div>
                  <div className={classes.activityImage_secondary}>
                    <i className={iconClasses} />
                  </div>
                </div>
              </div>
            </div>
            <div className={classes.activityItem_contentSection}>
              <div className={classes.activityItem_contentWrapper}>
                <div className={cx(classes.activityItem_header, { [classes.u_ellipsis]: !isExpanded })}>
                  {isCallRailCall ? (
                    <span>{missiveName}</span>
                  ) : (
                    <span className={cx(classes.activityItem_headerContent, { [classes.u_ellipsis]: !isExpanded })}>
                      <span className={cx(classes.actor_display, classes.u_ellipsis)}>{agentNameToUse}</span>
                      {connectivePhrase}
                      <Link onClick={functionToShowSubject(activity)} underline="hover">
                        {activity.subjectName}
                      </Link>
                    </span>
                  )}
                </div>
                <div
                  className={classes.activityItem_date}
                  onClick={(event) => this.ignoreClickIfExpandedOrIsCallRailCall(event, isExpanded, isCallRailCall)}
                >
                  <time dateTime={dateIsoString} title={dateTitleString}>
                    {dateDisplayString}
                  </time>
                </div>
                <div
                  className={itemContentClasses}
                  onClick={(event) => this.ignoreClickIfExpandedOrIsCallRailCall(event, isExpanded, isCallRailCall)}
                >
                  {isExpanded ? (
                    isNote ? (
                      <NoteEditorPanel
                        noteId={activity.id}
                        page={page}
                        referrerOrgId={referrerOrgId}
                        noteDeletionFunc={() => this.deleteActivity(activity)}
                        closeEditorFunc={() => this.toggleExpansion(activity)}
                      />
                    ) : isText ? (
                      <FullSmsPanel
                        sms={activity}
                        page={page}
                        textDeletionFunc={() => this.deleteActivity(activity)}
                        closeEditorFunc={() => this.toggleExpansion(activity)}
                      />
                    ) : (
                      <ActivityEditorPanel
                        activity={activity}
                        page={page}
                        activityColumn={null}
                        client={client}
                        referrerOrgId={referrerOrgId}
                        contacts={contacts}
                        deleteFunc={(activity) => this.deleteActivity(activity)}
                        closeEditorFunc={() => this.toggleExpansion(activity)}
                      />
                    )
                  ) : isCallRailCall ? (
                    <span>
                      <CallRailPlayerWidget recordingUrl={call.recordingUrl} />
                    </span>
                  ) : (
                    <span className={cx(classes.textArea, 'textArea_flat', { [classes.u_ellipsis]: !isExpanded })}>
                      {activity.content}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className={classes.activityItem_footer} />
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({});
const actionProducers = {
  removeNoteOnPerson,
  updateMissive,
  deleteMissiveWithId,
  removeFirmOutreach,
  removeOutreach,
  removeOutreachFrom,
  clearTaskAccomplishmentInfo,
  setApiResponseInfo,
};
export default connect(mapStateToProps, actionProducers)(withStyles(activityListStyles)(ActivityItem));
